/**
 * ImportGeneralIAModal — Sprint 5
 *
 * Modal unique : upload PDF ou image → l'IA classifie et extrait → brouillon.
 * L'utilisateur peut ensuite valider dans ImportDraftsModal.
 */
import { useState, useRef } from "react";
import { X, Upload, Loader2, FileText, Image as ImageIcon, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DOC_TYPE_LABELS = {
  facture_phyto: { label: "Facture phyto / bon de commande", color: "#059669", icon: "📦" },
  bon_traitement: { label: "Bon de traitement", color: "#0891B2", icon: "💧" },
  analyse_sol: { label: "Analyse de sol", color: "#7C3AED", icon: "🧪" },
  inconnu: { label: "Type non identifié", color: "#B45309", icon: "❓" },
};

export default function ImportGeneralIAModal({ onClose, onDraftCreated }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch(`${API}/import/general`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Erreur lors de l'import IA");
        toast.error(data.detail || "Erreur lors de l'import IA");
        return;
      }
      setResult(data);
      toast.success(`Document analysé : ${DOC_TYPE_LABELS[data.doc_type]?.label || data.doc_type}`);
      if (onDraftCreated) onDraftCreated(data);
    } catch (e) {
      setError(String(e));
      toast.error("Erreur réseau — réessayez");
    } finally {
      setUploading(false);
    }
  };

  const nbLines = result?.extracted_lines?.length || 0;
  const typeInfo = result ? DOC_TYPE_LABELS[result.doc_type] || DOC_TYPE_LABELS.inconnu : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="import-ia-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b px-5 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-2">
            <div
              className="w-9 h-9 rounded-2xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #7C3AED, #5B21B6)" }}
            >
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Sprint 5</div>
              <div className="font-bold text-base" style={{ color: "#1A1A1A" }}>Import Général IA</div>
            </div>
          </div>
          <button
            data-testid="close-import-ia-btn"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-black/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {!result && !uploading && (
            <>
              <p className="text-sm text-gray-600 leading-relaxed">
                Téléversez une <strong>facture phyto</strong>, un <strong>bon de traitement</strong> ou une <strong>analyse de sol</strong>.
                L&apos;IA détectera automatiquement le type, extraira toutes les lignes et créera un brouillon à valider.
              </p>

              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="p-2 rounded-xl border" style={{ borderColor: "#059669", background: "#F0FDF4" }}>
                  <div className="text-lg mb-0.5">📦</div>
                  <div className="font-medium" style={{ color: "#059669" }}>Facture</div>
                </div>
                <div className="p-2 rounded-xl border" style={{ borderColor: "#0891B2", background: "#F0F9FF" }}>
                  <div className="text-lg mb-0.5">💧</div>
                  <div className="font-medium" style={{ color: "#0891B2" }}>Bon traitement</div>
                </div>
                <div className="p-2 rounded-xl border" style={{ borderColor: "#7C3AED", background: "#FAF5FF" }}>
                  <div className="text-lg mb-0.5">🧪</div>
                  <div className="font-medium" style={{ color: "#7C3AED" }}>Analyse sol</div>
                </div>
              </div>

              <div
                data-testid="import-ia-dropzone"
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ borderColor: "#7C3AED" }}
              >
                <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: "#7C3AED" }} />
                <p className="text-sm font-medium mb-1">Sélectionner un fichier</p>
                <p className="text-xs text-gray-500">PDF, JPG ou PNG · max 20 Mo</p>
                <input
                  ref={inputRef}
                  data-testid="import-ia-file-input"
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/*"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>

              <div className="text-[11px] text-gray-500 bg-gray-50 rounded-xl p-3 leading-relaxed">
                <strong>Modèles IA :</strong> GPT-5-mini (texte PDF) · GPT-4o Vision (scans/photos).<br />
                <strong>Règle absolue :</strong> l&apos;IA extrait mais ne recommande jamais de produit.
                Chaque ligne devra être validée avant d&apos;entrer au registre.
              </div>
            </>
          )}

          {uploading && (
            <div className="py-12 flex flex-col items-center gap-3 text-center" data-testid="import-ia-loading">
              <div className="relative">
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: "#7C3AED" }} />
                <Sparkles className="w-4 h-4 absolute -top-1 -right-1" style={{ color: "#FBBF24" }} />
              </div>
              <div>
                <p className="font-medium text-sm">Analyse IA en cours…</p>
                <p className="text-xs text-gray-500 mt-1">Classification + extraction des lignes</p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl border" style={{ borderColor: "#DC2626", background: "#FEF2F2" }} data-testid="import-ia-error">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#DC2626" }} />
                <div className="text-sm" style={{ color: "#991B1B" }}>{error}</div>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="import-ia-result">
              <div
                className="p-4 rounded-2xl border-2"
                style={{ borderColor: typeInfo.color, background: typeInfo.color + "10" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5" style={{ color: typeInfo.color }} />
                  <div className="font-bold text-sm" style={{ color: typeInfo.color }}>
                    {typeInfo.icon} {typeInfo.label}
                  </div>
                  <span
                    className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{ background: typeInfo.color + "20", color: typeInfo.color }}
                  >
                    Confiance {(result.confidence * 100).toFixed(0)}%
                  </span>
                </div>

                {result.meta && Object.values(result.meta).some(Boolean) && (
                  <div className="text-xs space-y-0.5 text-gray-700 mb-2">
                    {result.meta.supplier && <div>• Fournisseur : <strong>{result.meta.supplier}</strong></div>}
                    {result.meta.invoice_date && <div>• Date : <strong>{result.meta.invoice_date}</strong></div>}
                    {result.meta.invoice_number && <div>• N° : <strong>{result.meta.invoice_number}</strong></div>}
                    {result.meta.operator_name && <div>• Opérateur : <strong>{result.meta.operator_name}</strong></div>}
                  </div>
                )}

                {nbLines > 0 && (
                  <div className="text-xs bg-white rounded-lg p-2">
                    <strong>{nbLines}</strong> ligne{nbLines > 1 ? "s" : ""} extraite{nbLines > 1 ? "s" : ""} à valider
                  </div>
                )}
                {result.doc_type === "analyse_sol" && (
                  <div className="text-xs bg-white rounded-lg p-2">
                    Analyse de sol prête — sélectionnez la parcelle cible à la validation.
                  </div>
                )}
              </div>

              <button
                data-testid="import-ia-open-validation"
                onClick={() => {
                  if (onDraftCreated) onDraftCreated(result, { openValidation: true });
                  onClose();
                }}
                className="w-full py-3 rounded-2xl text-white font-medium text-sm shadow-md hover:shadow-lg transition-all"
                style={{ background: "linear-gradient(135deg, #7C3AED, #5B21B6)" }}
              >
                Ouvrir la validation ({nbLines || 1} ligne{nbLines > 1 ? "s" : ""})
              </button>
              <button
                onClick={() => { setResult(null); setError(null); }}
                className="w-full py-2 text-xs text-gray-500 hover:text-gray-700"
                data-testid="import-ia-upload-another"
              >
                Importer un autre document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
